import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Award, Download, Sparkles, Lightbulb, User, DollarSign, Palette,
  Megaphone, Users, Mic, Briefcase, Zap, FileCheck, Eye, EyeOff,
  Share2, Copy, Check, Trash2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEHOLDER = "✏️ Keep building this section!";

function Section({ icon, title, complete, children, testId }) {
  return (
    <div
      className={`rounded-xl p-4 border-2 ${complete ? "bg-white border-purple-200" : "bg-gray-50 border-dashed border-gray-300"}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-purple-600">{icon}</span>
        <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide">{title}</h4>
      </div>
      <div className={complete ? "text-gray-800" : "text-gray-500 italic text-sm"}>
        {complete ? children : PLACEHOLDER}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  if (!value) return null;
  return (
    <div className="text-sm">
      <span className="font-semibold text-gray-700">{label}:</span>{" "}
      <span className="text-gray-800">{value}</span>
    </div>
  );
}

function StudentJourneyMap() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shares, setShares] = useState([]);
  const [creatingShare, setCreatingShare] = useState(false);
  const [shareLabel, setShareLabel] = useState("");
  const [shareDays, setShareDays] = useState(60);
  const [copiedToken, setCopiedToken] = useState("");

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/cte/me/journey-map`);
      setData(res.data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/cte/me/journey-map/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mini_business_plan.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { /* ignore */ }
    finally { setDownloading(false); }
  };

  const loadShares = async () => {
    try {
      const res = await axios.get(`${API}/student/journey-map/shares`);
      setShares(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const openShareModal = async () => {
    setShowShareModal(true);
    await loadShares();
  };

  const createShare = async () => {
    setCreatingShare(true);
    try {
      await axios.post(`${API}/student/journey-map/share/create`, {
        label: shareLabel || null,
        expires_in_days: shareDays,
      });
      setShareLabel("");
      await loadShares();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Failed to create share link.");
    } finally {
      setCreatingShare(false);
    }
  };

  const revokeShare = async (token) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Revoke this share link? Anyone using it will lose access.")) return;
    try {
      await axios.post(`${API}/student/journey-map/share/${token}/revoke`);
      await loadShares();
    } catch (e) { /* ignore */ }
  };

  const copyShareLink = async (token) => {
    const url = `${window.location.origin}/journey/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(""), 2000);
    } catch (e) { /* ignore */ }
  };

  if (loading || !data) return null;

  // Hide if student hasn't started Module 1 at all
  if (!data.sections?.entrepreneur_profile && data.completion?.completed_sessions === 0) return null;

  const s = data.sections || {};
  const c = data.completion || {};
  const profile = s.entrepreneur_profile;
  const persona = s.customer_persona;
  const money = s.money_plan;
  const brand = s.brand_identity;
  const mk = s.marketing_channels;
  const team = s.team_strengths;
  const pitch = s.pitch;

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8 border-2 border-purple-200" data-testid="journey-map">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 px-6 py-4 text-white">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest opacity-90">{t('journeyMap.title')}</div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Award className="w-6 h-6" />
              {s.business_name || t('journeyMap.fallbackName')}
            </h2>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{c.percent}%</div>
            <div className="text-xs opacity-90">{t('journeyMap.sessionsOf', { done: c.completed_sessions, total: c.total_sessions })}</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {c.all_complete && (
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3 mb-5 text-emerald-800 text-sm font-semibold flex items-center gap-2" data-testid="jm-all-complete-banner">
            {t('journeyMap.allCompleteBanner')}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
          <p className="text-sm text-gray-600 max-w-2xl">
            {t('journeyMap.shareBlurb')}
          </p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 px-3 py-2 bg-white border-2 border-purple-300 text-purple-700 text-sm font-semibold rounded-lg hover:bg-purple-50"
              data-testid="jm-toggle-expand-btn"
            >
              {expanded ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {expanded ? t('journeyMap.collapse') : t('journeyMap.expand')}
            </button>
            <button
              onClick={openShareModal}
              className="flex items-center gap-1 px-3 py-2 bg-white border-2 border-purple-400 text-purple-700 text-sm font-semibold rounded-lg hover:bg-purple-50"
              data-testid="jm-share-btn"
            >
              <Share2 className="w-4 h-4" />
              {t('journeyMap.shareLink')}
            </button>
            <button
              onClick={downloadPDF}
              disabled={downloading}
              className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg disabled:opacity-50"
              data-testid="jm-download-pdf-btn"
            >
              <Download className="w-4 h-4" />
              {downloading ? t('journeyMap.generating') : t('journeyMap.downloadPdf')}
            </button>
          </div>
        </div>

        {/* Always-visible top row: profile + business basics */}
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Section icon={<Sparkles className="w-4 h-4" />} title="Entrepreneur Profile" complete={!!profile} testId="jm-profile">
            <div className="text-2xl">{profile?.icon} <span className="font-bold text-gray-900 text-base">{profile?.name}</span></div>
            <div className="text-xs text-gray-600 mt-1">⚡ {profile?.superpower}</div>
          </Section>
          <Section icon={<Lightbulb className="w-4 h-4" />} title="Identified Problem" complete={!!s.identified_problem} testId="jm-problem">
            <p className="text-sm">{s.identified_problem}</p>
          </Section>
        </div>

        {expanded && (
          <>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <Section icon={<Zap className="w-4 h-4" />} title="My Solution" complete={!!s.solution} testId="jm-solution">
                <p className="text-sm">{s.solution}</p>
              </Section>
              <Section icon={<User className="w-4 h-4" />} title="Customer Persona" complete={!!(persona && persona.name)} testId="jm-persona">
                <div className="space-y-1">
                  <KV label="Name" value={persona?.name} />
                  <KV label="Pain" value={persona?.pain} />
                  <KV label="Dream solution" value={persona?.words} />
                </div>
              </Section>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <Section icon={<Palette className="w-4 h-4" />} title="Brand Identity" complete={!!(brand && brand.brand_name)} testId="jm-brand">
                <div className="space-y-1">
                  <KV label="Name" value={brand?.brand_name} />
                  <KV label="Tagline" value={brand?.tagline} />
                  <KV label="Colors" value={[brand?.primary_color, brand?.secondary_color].filter(Boolean).join(" / ")} />
                  <KV label="Logo" value={brand?.logo_idea} />
                  <KV label="Voice" value={brand?.voice_3words} />
                </div>
              </Section>
              <Section icon={<DollarSign className="w-4 h-4" />} title="Money Plan" complete={!!(money && (money.unit_cost || money.price))} testId="jm-money">
                <div className="space-y-1">
                  <KV label="Cost / unit" value={money?.unit_cost ? `$${money.unit_cost}` : null} />
                  <KV label="Price / unit" value={money?.price ? `$${money.price}` : null} />
                  <KV label="Profit" value={money?.profit ? `$${money.profit}` : null} />
                  <KV label="Weekly goal" value={money?.weekly_goal ? `$${money.weekly_goal}` : null} />
                  <KV label="Splits" value={[money?.reinvest_pct && `Reinvest ${money.reinvest_pct}%`, money?.save_pct && `Save ${money.save_pct}%`, money?.spend_pct && `Spend ${money.spend_pct}%`].filter(Boolean).join(" · ")} />
                </div>
              </Section>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <Section icon={<Megaphone className="w-4 h-4" />} title="Marketing Channels" complete={!!(mk && mk.channel_1)} testId="jm-marketing">
                <div className="space-y-1">
                  <KV label="Main" value={mk?.channel_1} />
                  <KV label="Backup" value={mk?.channel_2} />
                  <KV label="Signature extra" value={mk?.signature_extra} />
                  <KV label="Story hook" value={mk?.brand_story} />
                </div>
              </Section>
              <Section icon={<Users className="w-4 h-4" />} title="Team & Leadership" complete={!!(team && team.my_role)} testId="jm-team">
                <div className="space-y-1">
                  <KV label="My role" value={team?.my_role} />
                  <KV label="Teammate" value={team?.teammate_role} />
                  <KV label="Mentor" value={team?.mentor} />
                  <KV label="Workflow" value={team?.workflow_summary} />
                </div>
              </Section>
            </div>

            {/* Pitch — full width, special highlight */}
            <div className="mb-3">
              <Section icon={<Mic className="w-4 h-4" />} title="Pitch Summary" complete={!!(pitch && pitch.full_pitch)} testId="jm-pitch">
                {pitch?.full_pitch && (
                  <p className="italic mb-2 whitespace-pre-wrap">"{pitch.full_pitch}"</p>
                )}
                <div className="text-xs text-gray-600 space-x-3">
                  {pitch?.launch_date && <span><strong>Launch:</strong> {pitch.launch_date}</span>}
                  {pitch?.day1_goal && <span><strong>Day-1 goal:</strong> {pitch.day1_goal}</span>}
                  {pitch?.next_30 && <span><strong>Next 30:</strong> {pitch.next_30}</span>}
                </div>
              </Section>
            </div>

            {/* Bottom row: pathways · skills · standards */}
            <div className="grid md:grid-cols-3 gap-3">
              <Section icon={<Briefcase className="w-4 h-4" />} title="Career Pathways" complete={!!(s.career_pathways && s.career_pathways.length > 0)} testId="jm-pathways">
                <ul className="space-y-1 text-sm">
                  {(s.career_pathways || []).slice(0, 4).map((p) => (
                    <li key={p.key}>
                      {p.icon} {p.name}{p.in_demand && " ⭐"}
                    </li>
                  ))}
                </ul>
              </Section>
              <Section icon={<Zap className="w-4 h-4" />} title="Top Skills" complete={!!(s.top_employability_skills && s.top_employability_skills.length > 0)} testId="jm-skills">
                <ul className="space-y-1 text-sm">
                  {(s.top_employability_skills || []).map((sk) => (
                    <li key={sk.id}>
                      <span className="font-semibold">{sk.title}</span>{" "}
                      <span className="text-xs text-gray-500">({sk.id})</span>
                    </li>
                  ))}
                </ul>
              </Section>
              <Section icon={<FileCheck className="w-4 h-4" />} title="Standards Alignment" complete={!!(s.standards_alignment?.nevada_cte?.length || s.standards_alignment?.cctc?.length)} testId="jm-standards">
                <div className="text-xs space-y-2">
                  {s.standards_alignment?.nevada_cte?.length > 0 && (
                    <div><span className="font-bold text-orange-700">Nevada CTE:</span>{" "}
                      {s.standards_alignment.nevada_cte.map((x) => x.split("—")[0].replace("Nevada CTE", "").trim()).join(" · ")}
                    </div>
                  )}
                  {s.standards_alignment?.cctc?.length > 0 && (
                    <div><span className="font-bold text-blue-700">CCTC:</span>{" "}
                      {s.standards_alignment.cctc.map((x) => x.split("—")[0].replace("CCTC", "").trim()).join(" · ")}
                    </div>
                  )}
                  {s.standards_alignment?.academic_credit_subjects?.length > 0 && (
                    <div><span className="font-bold text-indigo-700">Academic credit:</span>{" "}
                      {s.standards_alignment.academic_credit_subjects.join(", ")}
                    </div>
                  )}
                </div>
              </Section>
            </div>
          </>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowShareModal(false)}
          data-testid="jm-share-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Share2 className="w-5 h-5 text-purple-600" /> Share Your Mini Business Plan
              </h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                aria-label="Close"
                data-testid="jm-share-modal-close"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              Create a <strong>safe, read-only public link</strong> to share with parents, mentors, or pitch judges — no
              login required. Only your <strong>first name</strong> is shown. You can revoke any link anytime.
            </p>

            {/* Create form */}
            <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-5">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Label (optional, helps you tell links apart)
              </label>
              <input
                type="text"
                value={shareLabel}
                onChange={(e) => setShareLabel(e.target.value)}
                placeholder="e.g., For Mom, Pitch Night, Career Fair"
                className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg focus:outline-none focus:border-purple-500"
                data-testid="jm-share-label-input"
              />
              <div className="mt-3">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Link valid for
                </label>
                <div className="flex gap-2 flex-wrap" role="radiogroup" data-testid="jm-share-expiry-picker">
                  {[30, 60, 90].map((d) => {
                    const selected = shareDays === d;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setShareDays(d)}
                        className={`px-4 py-1.5 rounded-full text-sm font-semibold border-2 transition-all ${
                          selected
                            ? "bg-purple-600 border-purple-600 text-white shadow-md"
                            : "bg-white border-purple-200 text-purple-700 hover:border-purple-400"
                        }`}
                        data-testid={`jm-share-days-${d}`}
                        aria-checked={selected}
                        role="radio"
                      >
                        {d} days
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-gray-600">
                  Expires <strong>{new Date(Date.now() + shareDays * 86400000).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</strong>.
                </p>
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  onClick={createShare}
                  disabled={creatingShare}
                  className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg disabled:opacity-50"
                  data-testid="jm-create-share-btn"
                >
                  {creatingShare ? "Creating..." : "Create Share Link"}
                </button>
              </div>
            </div>

            {/* Existing links */}
            <div data-testid="jm-share-list">
              {shares.length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-6">
                  No share links yet. Create one above.
                </div>
              ) : (
                <div className="space-y-3">
                  {shares.map((sh) => {
                    const url = `${window.location.origin}/journey/${sh.token}`;
                    const expDt = sh.expires_at ? new Date(sh.expires_at) : null;
                    const isExpired = expDt && expDt < new Date();
                    const inactive = sh.revoked || isExpired;
                    return (
                      <div
                        key={sh.token}
                        className={`border-2 rounded-xl p-3 ${
                          inactive ? "bg-gray-50 border-gray-200 opacity-60" : "bg-white border-purple-200"
                        }`}
                        data-testid={`jm-share-row-${sh.token.slice(0, 8)}`}
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 text-sm truncate">
                              {sh.label || "Untitled link"}
                              {sh.revoked && (
                                <span className="ml-2 text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                  REVOKED
                                </span>
                              )}
                              {!sh.revoked && isExpired && (
                                <span className="ml-2 text-xs font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                                  EXPIRED
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-3 mt-0.5 flex-wrap">
                              <span>Created {new Date(sh.created_at).toLocaleDateString()}</span>
                              {expDt && (
                                <span className={isExpired ? "text-orange-600 font-semibold" : "text-gray-500"}>
                                  {isExpired ? "Expired" : "Valid through"}{" "}
                                  {expDt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {sh.view_count || 0} views
                              </span>
                            </div>
                          </div>
                          {!inactive && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => copyShareLink(sh.token)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white text-xs font-semibold rounded-lg"
                                data-testid={`jm-copy-share-${sh.token.slice(0, 8)}`}
                              >
                                {copiedToken === sh.token ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                {copiedToken === sh.token ? "Copied" : "Copy Link"}
                              </button>
                              <button
                                onClick={() => revokeShare(sh.token)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-white border border-red-300 text-red-700 text-xs font-semibold rounded-lg hover:bg-red-50"
                                data-testid={`jm-revoke-share-${sh.token.slice(0, 8)}`}
                                title="Revoke this link"
                              >
                                <Trash2 className="w-3 h-3" />
                                Revoke
                              </button>
                            </div>
                          )}
                        </div>
                        {!inactive && (
                          <div className="mt-2 bg-gray-50 border border-gray-200 rounded p-2 text-xs text-gray-700 font-mono truncate" title={url}>
                            {url}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <p className="text-xs text-gray-500 mt-4 flex items-center gap-1">
              🔒 Public links show only your first name. Email, last name, class, and account ID are never shared.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentJourneyMap;
